import InfoRounded from '@mui/icons-material/InfoRounded';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { RHFSwitchField } from '@openx/components-core/lib/SwitchField/RHFSwitchField.tsx';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form.ts';
import { ACL_TYPE } from '@openx/types/aclType.ts';
import type { DealFormData } from '@openx/types/deal.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators.ts';
import { type JSX, useEffect, useRef } from 'react';
import { type Path, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDealFormContext } from '../../DealForm.context.tsx';
import { ThirdPartyFeeConfigs } from './components/ThirdPartyFeeConfigs.tsx';

type CuratedDealFeesFormSectionInteractiveProps = {
  isEdit: boolean;
};

export const CuratedDealFeesFormSectionInteractive = ({
  isEdit,
}: CuratedDealFeesFormSectionInteractiveProps): JSX.Element | null => {
  const { thirdPartyPartnerOptionsFetch } = useDealFormContext();
  const { getValues, setValue, trigger } = useFormContext<DealFormData>();
  const { useIsAllowed } = usePermissionContext();
  const { isAllowed } = useIsAllowed();
  const { current: isFilledOut } = useRef<boolean>(getValues(`third_party_fees_config_enabled`) && isEdit);
  const { t } = useTranslation();

  const hasPermissions = isAllowed(isAllowedByCompiledAcl(ACL_TYPE.THIRD_PARTY_FEES_WRITE));

  const observableAccountUid = useWatch<DealFormData, 'account_uid'>({ name: 'account_uid' });
  const observableThirdPartyFeesConfigEnabled = useWatch<DealFormData, 'third_party_fees_config_enabled'>({
    name: 'third_party_fees_config_enabled',
  });

  // clear fee values and trigger validation when switch is disabled to prevent stale cross-field validation errors
  useEffect(() => {
    if (!observableThirdPartyFeesConfigEnabled) {
      setValue('third_party_fees_config.0.gross_share', '', { shouldDirty: false });
      setValue('third_party_fees_config.0.gross_cpm_cap', '', { shouldDirty: false });
      trigger('third_party_fees_config.0.gross_share');
    }
  }, [observableThirdPartyFeesConfigEnabled, setValue, trigger]);

  useEffect(() => {
    // only fetch options when the user can use them
    if (hasPermissions) {
      thirdPartyPartnerOptionsFetch(observableAccountUid);
    }
  }, [hasPermissions, observableAccountUid, thirdPartyPartnerOptionsFetch]);

  if (!hasPermissions) {
    return null;
  }

  return (
    <FormSection
      data-test="openaudience-access"
      description={t('Fee sharing allows a selected partner to earn a margin based on media spend or CPM.')}
      divider
      rightColumnSize={RIGHT_COLUMN_WIDTH}
      title={t('Curated Deal Fees')}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
        }}
      >
        <RHFSwitchField
          data-test="enable-openaudience-access"
          disabled={isFilledOut}
          label={t('Enable curation fees')}
          margin="dense"
          name={'third_party_fees_config_enabled' satisfies Path<DealFormData>}
        />

        <Tooltip
          title={t(
            'When enabled, a portion of spend is shared with the selected partner based on the fee structure below.'
          )}
          placement="right"
        >
          <IconButton size="small">
            <InfoRounded />
          </IconButton>
        </Tooltip>
      </Stack>

      {observableThirdPartyFeesConfigEnabled && <ThirdPartyFeeConfigs isFilledOut={isFilledOut} />}
    </FormSection>
  );
};
